import logo from "assets/logo_verify.svg";
import home from "assets/Img-login-horizontal.png";
import { Box, Grid, Typography, Link as MuiLink, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { object, string, TypeOf } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import FormInput from "components/FormInput";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useForgotPasswordMutation } from "redux/api/authApi";

const LinkItem = styled(Link)`
  text-decoration: none;
  color: "secondary.main";
  &:hover {
    text-decoration: underline;
  }
`;

const forgotPasswordSchema = object({
  email: string()
    .min(1, "La dirección de correo electrónico es requerida")
    .email("Debe indicar una dirección de correo electrónico"),
});

export type ForgotPasswordInput = TypeOf<typeof forgotPasswordSchema>;


const ForgotPasswordPage = () => {
  const navigate = useNavigate();

  const methods = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  // 👇 API Login Mutation
  const [forgotPassword, { isLoading, isError, error, isSuccess }] =
    useForgotPasswordMutation();

  const {
    reset,
    setError,
    handleSubmit,
    formState: { isSubmitSuccessful },
  } = methods;

  useEffect(() => {
    if (isError && error) {
      if ("status" in error && error.status === 401) {
        setError("email", { type: "manual", message: "No existe este correo electrónico asociado a un usuario activo" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  const onSubmitHandler: SubmitHandler<ForgotPasswordInput> = ({ email }) => {
    // 👇 Executing the forgotPassword Mutation
    forgotPassword({ email });
  };

  const navigateToLogin = () => {
    navigate("/login");
  };

  if (isSuccess) {
    return (
      <Box
      sx={{
        width: "80%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: "3rem",
        "@media (min-width:900px)": {
          width: "80%", // Para md
        },
        "@media (min-width:1200px)": {
          width: "60%",
        },
        "@media (min-width:1536px)": {
          width: "60%", // Para xl
        },
      }}
    >
         <Box
           sx={{
            width: "40%",
            marginBottom: "3rem",
            "@media (min-width:900px)": {
              width: "90%", // Para md
            },
            "@media (min-width:1200px)": {
              width: "80%",
            },
            "@media (min-width:1536px)": {
              width: "70%", // Para xl
            },
          }}
          >
            <img src={logo} className="img-responsive" alt="Logo" />
          </Box>
          <Box sx={{ width: "100%" }}>
          <Typography
             component="h1"
             color="primary"
             sx={{
               fontStyle: "normal",
               fontWeight: 600,
               lineHeight: "1.8125rem",
               width: "100%",
               mb: ".3rem",
               textAlign: "center",
               fontSize: { xs: "1.4rem", xl: "1.4rem" },
             }}
          >
            Recuperar contraseña
          </Typography>
          <Typography
             variant="body1"
             component="h2"
             sx={{
               color: "#A7ABBB",
               mb: 4,
               fontSize: { xs: ".8rem", xl: ".8rem" },
               fontStyle: "normal",
               fontWeight: 500,
               textAlign: "center",
               lineHeight: "0.9375rem" /* 93.75% */,
             }}
          >
            Te hemos enviado un correo electrónico de recuperación de contraseña
          </Typography>
          <Button sx={{ mt: 3, width: "100%" , fontSize: "1.1rem"}} onClick={navigateToLogin}>
            Volver a inicio de sesión
          </Button>
      </Box>
      </Box>        );
  }

  return (
    <Box
    sx={{
      width: "80%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "3rem",
      "@media (min-width:900px)": {
        width: "80%", // Para md
      },
      "@media (min-width:1200px)": {
        width: "60%",
      },
      "@media (min-width:1536px)": {
        width: "60%", // Para xl
      },
    }}
  >
       <Box
            sx={{
              width: "40%",
              marginBottom: "3rem",
              "@media (min-width:900px)": {
                width: "90%", // Para md
              },
              "@media (min-width:1200px)": {
                width: "80%",
              },
              "@media (min-width:1536px)": {
                width: "70%", // Para xl
              },
            }}
          >
            <img src={logo} className="img-responsive" alt="Logo" />
          </Box>
          <Box sx={{ width: "100%" }}>
        <Typography
          component="h1"
          color="primary"
          sx={{
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "1.8125rem",
            width: "100%",
            mb: ".3rem",
            fontSize: { xs: "1.4rem", xl: "1.4rem" },
          }}
        >
          Recuperar contraseña
        </Typography>
        <Typography
              variant="body1"
              component="h2"
              sx={{
                color: "#A7ABBB",
                mb: 4,
                fontSize: { xs: ".8rem", xl: ".8rem" },
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "0.9375rem" /* 93.75% */,
              }}
        >
          Introduce tu dirección de correo electrónico y te enviaremos un enlace
          para restablecer tu contraseña
        </Typography>

        <FormProvider {...methods}>
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmitHandler)}
            noValidate
            autoComplete="off"
            width="100%"
          >
            <FormInput
              name="email"
              label="Dirección correo electrónico"
              type="email"
              sx={{
                mb: 2,
                ".MuiTypography-body2": {
                  fontWeight: 600,
                  fontSize: ".9rem",
                  fontStyle: "normal",
                  lineHeight: "1.8125rem",
                },
                ".MuiInput-root": {
                  fontSize: ".9rem",
                  padding: 0,
                  height: "32px",
                  lineHeight: "1.8125rem",
                  color: "#718096",
                },
              }}
            />
          
              <Button
                   variant="contained"
                   sx={{ mt: 3, width: "100%" , fontSize: "1.1rem"}}
                     disableElevation
                   type="submit"
              >
                Recuperar Contraseña
              </Button>
            </Box>
            <Typography
              sx={{ fontSize: "0.9rem", mt: "1rem", textAlign: "center" }}
            >
         
            </Typography>
        </FormProvider>
        </Box>
  </Box>
  );
};

export default ForgotPasswordPage;
